import * as THREE from "three";

const CYCLE_DURATION = 600; // 10 minutes in seconds
const START_TIME = 0.3; // early morning

// Day runs from 0.2 to 0.8 (60% of cycle = 6 min)
// Night runs from 0.8 to 0.2 (40% of cycle = 4 min)
// We want night to be shorter: compress night to 25% of cycle
// Use time warping: night progresses faster

export class DayNightCycle {
  private scene: THREE.Scene;
  private dirLight: THREE.DirectionalLight;
  private ambientLight: THREE.AmbientLight;
  private hemiLight: THREE.HemisphereLight;
  private time: number = START_TIME;
  private stars: THREE.Points;
  private starMaterial: THREE.PointsMaterial;
  pointLightMode = false;

  // Pre-allocated color objects to avoid GC
  private skyColor = new THREE.Color();
  private dirColor = new THREE.Color();
  private tempColorA = new THREE.Color();
  private tempColorB = new THREE.Color();

  // Sky color constants
  private readonly COLOR_NIGHT = new THREE.Color(0x0a0a2a);
  private readonly COLOR_SUNRISE = new THREE.Color(0xffaa55);
  private readonly COLOR_DAY = new THREE.Color(0x87ceeb);
  private readonly COLOR_SUNSET = new THREE.Color(0xff8844);

  // Dir light color constants
  private readonly DIR_WARM = new THREE.Color(0xffaa55);
  private readonly DIR_WHITE = new THREE.Color(0xffffff);
  private readonly DIR_SUNSET_WARM = new THREE.Color(0xff8844);

  constructor(
    scene: THREE.Scene,
    dirLight: THREE.DirectionalLight,
    ambientLight: THREE.AmbientLight,
    hemiLight: THREE.HemisphereLight
  ) {
    this.scene = scene;
    this.dirLight = dirLight;
    this.ambientLight = ambientLight;
    this.hemiLight = hemiLight;

    // Create stars
    const starCount = 200;
    const starGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(starCount * 3);
    const radius = 150;

    for (let i = 0; i < starCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      // Only upper hemisphere
      const adjustedPhi = phi * 0.5;

      positions[i * 3] = radius * Math.sin(adjustedPhi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.cos(adjustedPhi);
      positions[i * 3 + 2] = radius * Math.sin(adjustedPhi) * Math.sin(theta);
    }

    starGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );

    this.starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.8,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      sizeAttenuation: true,
    });

    this.stars = new THREE.Points(starGeometry, this.starMaterial);
    this.scene.add(this.stars);

    // Initial update
    this.updateLighting();
  }

  update(dt: number): void {
    // Night passes faster (2x speed) to make it shorter
    const isNight = this.time < 0.2 || this.time > 0.8;
    const speedMultiplier = isNight ? 2.0 : 1.0;
    this.time += (dt / CYCLE_DURATION) * speedMultiplier;
    // Wrap around
    this.time = this.time % 1;
    if (this.time < 0) this.time += 1;

    this.updateLighting();
  }

  getTimeOfDay(): number {
    return this.time;
  }

  setTime(t: number): void {
    this.time = t % 1;
    if (this.time < 0) this.time += 1;
    this.updateLighting();
  }

  getTimeString(): string {
    // Map 0-1 to 24-hour clock (0 = midnight = 00:00, 0.5 = noon = 12:00)
    const totalMinutes = Math.floor(this.time * 24 * 60);
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  isNight(): boolean {
    return this.time < 0.2 || this.time > 0.8;
  }

  getSkyBrightness(): number {
    // Sky brightness scales sky light levels for time of day
    // Night: 0.35 → sky 15 becomes effective level 5 → dim moonlight
    // Day: 1.0 → sky 15 stays at full brightness
    const t = this.time;
    if (t < 0.2 || t > 0.8) {
      return 0.35;
    } else if (t < 0.35) {
      const factor = this.smoothstep((t - 0.2) / 0.15);
      return 0.35 + factor * 0.65;
    } else if (t > 0.65) {
      const factor = this.smoothstep(1 - (t - 0.65) / 0.15);
      return 0.35 + factor * 0.65;
    }
    return 1.0;
  }

  private updateLighting(): void {
    const t = this.time;

    // --- Sky color ---
    this.computeSkyColor(t);
    this.scene.background = this.skyColor.clone();
    if (this.scene.fog) {
      (this.scene.fog as THREE.Fog).color.copy(this.skyColor);
    }

    // --- Directional light intensity & color ---
    let dirIntensity: number;
    if (t < 0.2 || t > 0.8) {
      // Night
      dirIntensity = 0;
    } else if (t < 0.35) {
      // Sunrise ramp: 0.2 -> 0.35
      dirIntensity = this.smoothstep((t - 0.2) / 0.15);
    } else if (t > 0.65) {
      // Sunset ramp: 0.65 -> 0.8
      dirIntensity = this.smoothstep(1 - (t - 0.65) / 0.15);
    } else {
      dirIntensity = 1.0;
    }
    this.dirLight.intensity = dirIntensity * 0.6;

    // Directional light color: warm at sunrise/sunset, white at noon
    this.computeDirLightColor(t);
    this.dirLight.color.copy(this.dirColor);

    // Directional light position: sun rotates in xz plane
    const sunAngle = t * Math.PI * 2 - Math.PI / 2; // 0.25 = east, 0.5 = overhead-ish
    const sunRadius = 50;
    this.dirLight.position.set(
      Math.cos(sunAngle) * sunRadius,
      40,
      Math.sin(sunAngle) * sunRadius
    );

    // --- Ambient light ---
    if (this.pointLightMode) {
      // PointLight mode: low ambient so real PointLights create visible contrast
      if (t < 0.2 || t > 0.8) {
        this.ambientLight.intensity = 0.15;
      } else if (t < 0.35) {
        const factor = this.smoothstep((t - 0.2) / 0.15);
        this.ambientLight.intensity = 0.15 + factor * 0.25;
      } else if (t > 0.65) {
        const factor = this.smoothstep(1 - (t - 0.65) / 0.15);
        this.ambientLight.intensity = 0.15 + factor * 0.25;
      } else {
        this.ambientLight.intensity = 0.4;
      }
      this.hemiLight.intensity = 0;
    } else {
      // Lightmap mode: high ambient so vertex-color lightmap passes through faithfully
      if (t < 0.2 || t > 0.8) {
        this.ambientLight.intensity = 0.9;
      } else if (t < 0.35) {
        const factor = this.smoothstep((t - 0.2) / 0.15);
        this.ambientLight.intensity = 0.9 + factor * 0.1;
      } else if (t > 0.65) {
        const factor = this.smoothstep(1 - (t - 0.65) / 0.15);
        this.ambientLight.intensity = 0.9 + factor * 0.1;
      } else {
        this.ambientLight.intensity = 1.0;
      }
      this.hemiLight.intensity = 0;
    }

    // --- Stars ---
    let starOpacity: number;
    if (t < 0.15 || t > 0.85) {
      starOpacity = 1.0;
    } else if (t < 0.25) {
      starOpacity = 1.0 - this.smoothstep((t - 0.15) / 0.1);
    } else if (t > 0.75) {
      starOpacity = this.smoothstep((t - 0.75) / 0.1);
    } else {
      starOpacity = 0;
    }
    this.starMaterial.opacity = starOpacity;
    this.stars.visible = starOpacity > 0.01;
  }

  private computeSkyColor(t: number): void {
    if (t < 0.2 || t >= 0.8) {
      // Night
      this.skyColor.copy(this.COLOR_NIGHT);
    } else if (t < 0.275) {
      // Sunrise part 1: night -> warm orange (0.2 to 0.275)
      const factor = this.smoothstep((t - 0.2) / 0.075);
      this.skyColor.lerpColors(this.COLOR_NIGHT, this.COLOR_SUNRISE, factor);
    } else if (t < 0.35) {
      // Sunrise part 2: warm orange -> day blue (0.275 to 0.35)
      const factor = this.smoothstep((t - 0.275) / 0.075);
      this.skyColor.lerpColors(this.COLOR_SUNRISE, this.COLOR_DAY, factor);
    } else if (t < 0.65) {
      // Day
      this.skyColor.copy(this.COLOR_DAY);
    } else if (t < 0.725) {
      // Sunset part 1: day blue -> warm orange (0.65 to 0.725)
      const factor = this.smoothstep((t - 0.65) / 0.075);
      this.skyColor.lerpColors(this.COLOR_DAY, this.COLOR_SUNSET, factor);
    } else {
      // Sunset part 2: warm orange -> night (0.725 to 0.8)
      const factor = this.smoothstep((t - 0.725) / 0.075);
      this.skyColor.lerpColors(this.COLOR_SUNSET, this.COLOR_NIGHT, factor);
    }
  }

  private computeDirLightColor(t: number): void {
    if (t < 0.2 || t > 0.8) {
      this.dirColor.copy(this.DIR_WARM);
    } else if (t < 0.35) {
      const factor = this.smoothstep((t - 0.2) / 0.15);
      this.dirColor.lerpColors(this.DIR_WARM, this.DIR_WHITE, factor);
    } else if (t > 0.65) {
      const factor = this.smoothstep(1 - (t - 0.65) / 0.15);
      this.dirColor.lerpColors(this.DIR_SUNSET_WARM, this.DIR_WHITE, factor);
    } else {
      this.dirColor.copy(this.DIR_WHITE);
    }
  }

  private smoothstep(x: number): number {
    const clamped = Math.max(0, Math.min(1, x));
    return clamped * clamped * (3 - 2 * clamped);
  }
}
